import { useEffect, useMemo, useState } from 'react';

import { Alert, Badge, Button, ClienteV2Combobox, HelpTip, Input, Select } from '@/components/ui';
// 🔴 O MESMO filtro do disparo por grupo (Comunicados) — extraído em 12/09,
// não copiado. Ver `FiltroDeGrupos.tsx`.
import { useFiltroDeGrupos, type EstadoDoBot } from '@/components/grupos/FiltroDeGrupos';
import { SeloDoBot } from '@/components/grupos/SeloDoBot';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';

import { nomeDoGrupo } from './grupo-nome';

/**
 * A lista de grupos do WhatsApp — **tudo alterável, sempre**.
 *
 * 🔴 MULTIGRUPO (mig 558): o vínculo virou uma LISTA de destinatários, e cada
 * destinatário pode ser **cliente do v2** ou **cliente do Auvo** — os clientes
 * de contratantes (Route) não existem no v2 nem no Omie, e não vão existir.
 * Um grupo acompanha N clientes; um cliente é acompanhado por N grupos; grupo
 * duplicado do mesmo cliente NÃO é erro (portaria e síndico são legítimos).
 *
 * 🔴 O grupo interno escolhe entre DOIS papéis: supervisão (entra nos avisos de
 * TODOS os clientes — o que chega é o que os interruptores deixam) · silencioso. ⚠️ O terceiro, **"destino de sobra", foi REMOVIDO em
 * 13/09** por decisão do Matheus: nunca foi usado (zero grupos), e aviso de
 * cliente sem grupo mapeado **não sai** — fica na trilha, com o motivo.
 *
 * ⚠️ **O nome que manda é o da Evolution**, não o do sistema antigo — para
 * conferir um vínculo, o que serve é o nome que o Matheus vê no celular.
 * ⚠️ Vínculo novo nasce como PROPOSTA: só dispara depois de Conferido.
 */

export interface DestinatarioDoGrupo {
  /** 'tipo-tarefa' (mig 576): o grupo acompanha TAREFAS de um tipo — o
   *  `codigo` é o marcador que o nome do tipo contém (ex.: "- Route"). */
  sistema: 'v2' | 'auvo' | 'tipo-tarefa';
  codigo: string;
  nome: string | null;
  conferido: boolean;
  /** Quem propôs (ou a marca da sugestão automática, mig 706) — o servidor já mandava; a tela passa a mostrar. */
  vinculadoPor?: string | null;
}

export interface Grupo {
  grupoId: string;
  nomeNoV1: string | null;
  nomeNaEvolution: string | null;
  destinatarios: DestinatarioDoGrupo[];
  semPar: boolean;
  interno: boolean;
  papel: 'supervisao' | 'silencioso' | null;
  foraDoBot: boolean;
  /** Quando a Evolution confirmou o bot no grupo pela última vez — NULO =
   *  nunca conferido (que NÃO é o mesmo que "dentro"). */
  vistoNaEvolutionIso?: string | null;
  sugestaoClienteId: string | null;
  sugestaoClienteNome: string | null;
  sugestaoAuvoCodigo: string | null;
  sugestaoAuvoNome: string | null;
  sugestaoMotivo: string | null;
}

const AJUDA_GRUPOS =
  'Um grupo não é "do cliente" ou "da IGM": um grupo ACOMPANHA uma lista de clientes. O do '
  + 'condomínio acompanha um; o de um contratante (como a Route) acompanha os clientes dele — e '
  + 'recebe os mesmos relatórios que o grupo de cada cliente. O laço vale para tudo: é por ele que '
  + 'um aviso sabe para onde ir, e um evento vai a TODOS os grupos que acompanham aquele cliente. '
  + 'Ligar não basta — enquanto ninguém marcar Conferido, nenhum aviso usa o laço. '
  + 'Um grupo também pode acompanhar um TIPO DE TAREFA (ex.: a Route — toda OS cujo tipo contém '
  + '"- Route" chega lá, além do grupo do cliente): a tarefa se declara sozinha, sem lista de '
  + 'clientes para manter. Avisos de monitoramento não têm tipo de tarefa e nunca vão a grupo de '
  + 'tipo. '
  + '"É da IGM" marca grupo INTERNO — e interno significa que ele NÃO recebe relatório de cliente '
  + 'nenhum: escolhe um papel (Supervisão acompanha todos os clientes, no que os avisos de '
  + '"O que este grupo recebe" deixarem; Silencioso não recebe nada, mesmo com avisos ligados). '
  + 'Grupo que deve receber os relatórios de alguns clientes não é '
  + 'interno — é um grupo com esses clientes acompanhados.';

const PAPEL_ROTULO: Record<string, string> = {
  supervisao: 'Supervisão — acompanha todos os clientes, no que os avisos deixarem',
  silencioso: 'Silencioso — não recebe nada, mesmo com avisos ligados',
};

/** "1 cliente" · "2 clientes e 1 tipo de tarefa" · "1 tipo de tarefa" —
 *  a linha diz o que o grupo acompanha, nas duas dimensões (mig 576). */
function resumoDoQueAcompanha(destinatarios: DestinatarioDoGrupo[]): string {
  const clientes = destinatarios.filter((d) => d.sistema !== 'tipo-tarefa').length;
  const tipos = destinatarios.length - clientes;
  const partes: string[] = [];
  if (clientes > 0) partes.push(clientes === 1 ? '1 cliente' : `${clientes} clientes`);
  if (tipos > 0) partes.push(tipos === 1 ? '1 tipo de tarefa' : `${tipos} tipos de tarefa`);
  const texto = partes.join(' e ');
  return clientes > 1 || tipos > 0 ? `${texto} — os avisos levam o nome do cliente` : texto;
}

/**
 * 🔴 "DE QUEM É" — a terceira coisa que a linha fechada tem de responder.
 *
 * ⚠️ Curto de propósito: a linha fechada é uma linha. O detalhe (quais
 * clientes, conferido ou proposta) vive dentro, onde há espaço.
 */
function deQuemE(g: Grupo): string {
  if (g.interno) return 'interno da IGM';
  const clientes = g.destinatarios.filter((d) => d.sistema !== 'tipo-tarefa');
  const tipos = g.destinatarios.filter((d) => d.sistema === 'tipo-tarefa');
  if (clientes.length === 0 && tipos.length === 0) {
    return g.semPar ? 'sem cliente' : 'sem cliente ainda';
  }
  if (clientes.length === 1 && tipos.length === 0) return clientes[0].nome ?? clientes[0].codigo;
  if (clientes.length === 0) {
    return tipos.length === 1 ? `tipo “${tipos[0].codigo}”` : `${tipos.length} tipos de tarefa`;
  }
  const base = clientes.length === 1 ? clientes[0].nome ?? clientes[0].codigo : `${clientes.length} clientes`;
  return tipos.length > 0 ? `${base} + tipo` : base;
}

export function GruposDoWhatsApp({
  grupos,
  onMudou,
  onAvisos,
}: {
  grupos: Grupo[];
  onMudou: () => void;
  /** Abre o diálogo "Avisos deste grupo" (opt-out por tipo). */
  onAvisos?: (grupoId: string) => void;
}) {
  const [erro, setErro] = useState<string | null>(null);
  /**
   * 🔴 A PAREDE (Frente 1, 12/09/2026).
   *
   * > **Matheus:** *"Em Integrações › WhatsApp, aba Grupos, limites e conexões
   * > está muito grande, extensa. Veja o que dá para fazer para diminuir,
   * > talvez o sistema de clicar e expandir."*
   *
   * ⚠️ Eram **52 grupos, cada um com seis controles à mostra** — para achar um,
   * ele rolava a tela inteira.
   *
   * 🔴 **E a armadilha, que já nos pegou duas vezes:** "começa fechado" foi
   * exatamente o que escondeu a trilha (#267). A diferença aqui é que **a linha
   * fechada JÁ RESPONDE a pergunta principal** — *o bot está neste grupo?* — e
   * o que se esconde são as AÇÕES, não a informação. Se um dia a linha fechada
   * deixar de dizer o estado do bot, o desenho voltou a estar errado.
   */
  const [abertoId, setAbertoId] = useState<string | null>(null);
  const [trocando, setTrocando] = useState<string | null>(null);
  /** O marcador de tipo sendo digitado, por grupo (mig 576). */
  const [marcadorTipo, setMarcadorTipo] = useState<Record<string, string>>({});
  const [clientesAuvo, setClientesAuvo] = useState<Array<{ codigo: string; nome: string }>>([]);

  // A lista do Auvo é pequena (centenas) e já está no banco — uma busca só.
  useEffect(() => {
    api
      .get<{ clientes: Array<{ codigo: string; nome: string }> }>('/api/v1/admin/whatsapp/clientes-auvo')
      .then((r) => setClientesAuvo(r.clientes))
      .catch(() => setClientesAuvo([]));
  }, []);

  async function chamar(caminho: string, corpo: unknown, metodo: 'post' | 'delete' = 'post') {
    setErro(null);
    try {
      if (metodo === 'delete') await api.delete(caminho);
      else await api.post(caminho, corpo);
      onMudou();
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErro(e.response?.erro ?? e.message);
    }
  }

  const url = (g: Grupo, sufixo: string) =>
    `/api/v1/admin/whatsapp/grupos/${encodeURIComponent(g.grupoId)}${sufixo}`;

  /**
   * O adaptador para o filtro compartilhado. ⚠️ `bot` é o TRI-ESTADO: sem
   * conferência na Evolution, `foraDoBot=false` NÃO é "dentro".
   */
  const filtraveis = useMemo(
    () =>
      grupos.map((g) => ({
        ...g,
        nome: nomeDoGrupo(g),
        acompanha: g.destinatarios.map((d) =>
          d.sistema === 'tipo-tarefa' ? `tipo ${d.codigo}` : (d.nome ?? d.codigo),
        ),
        clienteIds: g.destinatarios.filter((d) => d.sistema !== 'tipo-tarefa').map((d) => d.codigo),
        porTipo: g.destinatarios.some((d) => d.sistema === 'tipo-tarefa'),
        bot: (g.foraDoBot
          ? 'fora'
          : g.vistoNaEvolutionIso
            ? 'dentro'
            : 'nunca-conferido') as EstadoDoBot,
      })),
    [grupos],
  );
  const { filtrados, controles } = useFiltroDeGrupos(filtraveis, {
    idPrefixo: 'wa-grupos',
    testIdPrefixo: 'wa',
  });

  const naoConferidos = grupos.reduce(
    (n, g) => n + g.destinatarios.filter((d) => !d.conferido).length,
    0,
  );

  return (
    <div className="space-y-3" data-testid="wa-lista-grupos">
      {erro && <Alert variant="danger">{erro}</Alert>}

      {/* 🔴 Os vínculos importados são PROPOSTAS até o olho dele passar. */}
      {naoConferidos > 0 && (
        <Alert variant="warning" data-testid="wa-nao-conferidos">
          {naoConferidos === 1
            ? '1 vínculo ainda não foi conferido por você.'
            : `${naoConferidos} vínculos ainda não foram conferidos por você.`}{' '}
          Confira o nome do grupo e marque <strong>Conferido</strong>; enquanto isso, nenhum aviso
          automático usa esses vínculos.
        </Alert>
      )}

      <span className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          Grupos do WhatsApp
        </span>
        <HelpTip texto={AJUDA_GRUPOS} />
      </span>

      {/* 🔴 O filtro é o MESMO do disparo por grupo — busca por nome e
          estado do bot. Com 52 grupos, o acordeão sozinho ainda deixaria 52
          linhas: é o filtro que resolve de verdade. */}
      {controles}

      <div className="space-y-2">
        {filtrados.map((g) => (
          <div
            key={g.grupoId}
            className="rounded-lg border border-line p-3 dark:border-line-dark"
            data-testid={`wa-grupo-${g.grupoId}`}
          >
            {/* ─── 🔴 A LINHA FECHADA — e o que ela precisa responder sozinha:
                 o NOME · o ESTADO DO BOT · DE QUEM É. Nada aqui pode sumir
                 para dentro do acordeão: o que se esconde são as AÇÕES. ─── */}
            <button
              type="button"
              onClick={() => setAbertoId(abertoId === g.grupoId ? null : g.grupoId)}
              aria-expanded={abertoId === g.grupoId}
              data-testid={`wa-linha-${g.grupoId}`}
              className="flex w-full items-center gap-2 text-left"
            >
              <span className="w-3 shrink-0 text-ink-3 dark:text-ink-dark-3" aria-hidden="true">
                {abertoId === g.grupoId ? '▾' : '▸'}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                {nomeDoGrupo(g)}
              </span>
              <SeloDoBot
                bot={g.foraDoBot ? 'fora' : g.vistoNaEvolutionIso ? 'dentro' : 'nunca-conferido'}
                testId={`wa-selo-${g.grupoId}`}
              />
              {/* 🔴 `truncate` com teto, NÃO `shrink-0` (conserto de 12/09, à
                  noite). ⚠️ Com `shrink-0` e um nome de condomínio de 45
                  caracteres — "ASSOCIACAO DOS ADQUIRENTES DE LOTES DO JARDIM…"
                  — este `span` media **433px numa tela de 375**, e a PÁGINA
                  INTEIRA passava a rolar na horizontal. Foi a régua nova do
                  celular que pegou, no smoke pós-deploy: defeito meu, da
                  própria Frente 1.
                  O nome longo agora corta; o que ele responde — "de quem é" —
                  continua legível nos primeiros caracteres, e o nome inteiro
                  está dentro, ao abrir a linha. */}
              <span
                className="min-w-0 max-w-[45%] truncate text-right text-xs text-ink-3 dark:text-ink-dark-3"
                data-testid={`wa-dequem-${g.grupoId}`}
                title={deQuemE(g)}
              >
                {deQuemE(g)}
              </span>
            </button>

            {abertoId === g.grupoId && (
            <div className="mt-3 border-t border-line pt-3 dark:border-line-dark">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {/* ⚠️ O nome e o selo do bot NÃO se repetem aqui: eles moram na
                    linha fechada, que é onde precisam estar. O que fica é o
                    DETALHE — a data da conferência, o papel, a lista de
                    destinatários —, que só faz sentido com o grupo aberto. */}
                <div className="flex flex-wrap items-center gap-2">
                  {g.interno && (
                    <Badge variant="neutral" size="sm">Interno da IGM</Badge>
                  )}
                  {/* 🔴 O TRI-ESTADO do bot (09/09), por extenso: dentro · fora
                      · nunca conferido. `foraDoBot=false` sem conferência NÃO é
                      "dentro" — o número saiu dos grupos dos clientes, e dizer
                      verde sem ter perguntado à Evolution seria mentir. */}
                  {g.foraDoBot ? (
                    <Badge variant="danger" size="sm" data-testid={`wa-fora-${g.grupoId}`}>
                      O bot não está neste grupo
                    </Badge>
                  ) : g.vistoNaEvolutionIso ? (
                    <Badge variant="success" size="sm" data-testid={`wa-dentro-${g.grupoId}`}>
                      Bot no grupo · conferido {formatDateTime(g.vistoNaEvolutionIso)}
                    </Badge>
                  ) : (
                    <Badge variant="neutral" size="sm" data-testid={`wa-nao-conferido-${g.grupoId}`}>
                      Bot não conferido — use “Buscar grupos do WhatsApp”
                    </Badge>
                  )}
                </div>

                {/* 🔴 O SILÊNCIO EXPLICADO (Frente 6, 12/09/2026).
                    > **Matheus:** *"A Route não responde."*

                    ⚠️ A decisão é dele e está mantida — mas **silêncio sem
                    explicação é indistinguível de defeito**, e daqui a um mês
                    ninguém lembra o motivo. Um grupo que só acompanha TIPO DE
                    TAREFA não tem cliente; a resposta automática monta a frase
                    com o nome do cliente, e o porteiro recusa por variável
                    vazia (foi o `porteiro_recusou · "Faltou: cliente"` que
                    apareceu na trilha em 12/09). ❌ Não se criou texto próprio
                    para a Route: o que se criou foi a frase que diz por quê. */}
                {!g.interno &&
                  g.destinatarios.length > 0 &&
                  g.destinatarios.every((d) => d.sistema === 'tipo-tarefa') && (
                    <p
                      className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3"
                      data-testid={`wa-sem-resposta-automatica-${g.grupoId}`}
                    >
                      ⚠️ <strong>Este grupo não recebe a resposta automática de
                      fora do horário.</strong> Ela monta a frase com o nome do
                      cliente, e um grupo que acompanha só tipo de tarefa não
                      tem cliente. Os avisos de OS continuam chegando
                      normalmente.
                    </p>
                  )}

                <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                  {g.interno
                    ? (PAPEL_ROTULO[g.papel ?? 'silencioso'] ?? PAPEL_ROTULO.silencioso)
                    : g.destinatarios.length > 0
                      ? `Acompanha ${resumoDoQueAcompanha(g.destinatarios)}:`
                      : g.semPar
                        ? 'Marcado como sem cliente.'
                        : (g.sugestaoMotivo ?? 'Sem cliente.')}
                  {g.nomeNaEvolution && g.nomeNoV1 && g.nomeNaEvolution !== g.nomeNoV1 && (
                    <> · no sistema antigo: {g.nomeNoV1}</>
                  )}
                </p>

                {/* 🔴 A LISTA de destinatários — conferir e remover é POR vínculo. */}
                {g.destinatarios.length > 0 && (
                  <ul className="mt-1.5 space-y-1" data-testid={`wa-destinatarios-${g.grupoId}`}>
                    {g.destinatarios.map((d) => (
                      <li
                        key={`${d.sistema}:${d.codigo}`}
                        className="flex flex-wrap items-center gap-2 text-sm"
                        data-testid={`wa-dest-${g.grupoId}-${d.sistema}-${d.codigo}`}
                      >
                        <span className="text-ink-1 dark:text-ink-dark-1">
                          {d.sistema === 'tipo-tarefa'
                            ? `Tarefas cujo tipo contém “${d.codigo}”`
                            : (d.nome ?? d.codigo)}
                        </span>
                        {d.sistema === 'auvo' && (
                          <Badge variant="neutral" size="sm">do Auvo</Badge>
                        )}
                        {d.sistema === 'tipo-tarefa' && (
                          <Badge variant="info" size="sm">tipo de tarefa</Badge>
                        )}
                        {d.conferido ? (
                          <Badge variant="success" size="sm">Conferido</Badge>
                        ) : (
                          <Badge variant="warning" size="sm">Proposta</Badge>
                        )}
                        {/* 🔴 08/10/2026 (mig 706): confirmar às cegas é o mesmo que não confirmar — a PROPOSTA diz
                            de onde veio e qual é o código, para quem vai conferir. */}
                        {!d.conferido && d.vinculadoPor && (
                          <span
                            className="w-full text-xs text-ink-3 dark:text-ink-dark-3"
                            data-testid={`wa-origem-${g.grupoId}-${d.codigo}`}
                          >
                            {d.vinculadoPor.startsWith('sugestao-mig-706:')
                              ? `Sugestão do sistema (código ${d.codigo} no Auvo): ${d.vinculadoPor.slice('sugestao-mig-706:'.length).trim()}. Só passa a valer quando você conferir.`
                              : `Proposto por ${d.vinculadoPor} — ainda não conferido.`}
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant={d.conferido ? 'ghost' : 'primary'}
                          onClick={() =>
                            void chamar(
                              url(g, `/destinatarios/${d.sistema}/${encodeURIComponent(d.codigo)}/conferido`),
                              { conferido: !d.conferido },
                            )
                          }
                          data-testid={`wa-conferir-${g.grupoId}-${d.codigo}`}
                        >
                          {d.conferido ? 'Desmarcar' : 'Conferido'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            void chamar(
                              url(g, `/destinatarios/${d.sistema}/${encodeURIComponent(d.codigo)}`),
                              null,
                              'delete',
                            )
                          }
                          data-testid={`wa-remover-${g.grupoId}-${d.codigo}`}
                        >
                          Remover
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* ⚠️ Sem `shrink-0`: quebrar é melhor que empurrar a tela a 375px. */}
              <div className="flex flex-wrap items-center gap-2">
                {!g.interno && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setTrocando(trocando === g.grupoId ? null : g.grupoId);
                    }}
                    data-testid={`wa-trocar-${g.grupoId}`}
                  >
                    {g.destinatarios.length > 0 ? 'Acompanhar mais um' : 'Escolher cliente ou tipo'}
                  </Button>
                )}

                {!g.interno && g.destinatarios.length === 0 && !g.semPar && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void chamar(url(g, '/sem-par'), {})}
                    data-testid={`wa-sem-par-${g.grupoId}`}
                    title="Sai da fila sem virar vínculo falso."
                  >
                    Não tem cliente
                  </Button>
                )}

                {/* 🔴 13/09 — A PORTA, sem condição nenhuma.
                    ⚠️ Este botão exigia grupo de CLIENTE com vínculo; o irmão,
                    lá embaixo, exigia papel de SUPERVISÃO. O `Coletor IP - IGM`
                    é interno e nasceu silencioso: caía nos dois "não", e a tela
                    que respondia à pergunta dele nunca aparecia. Agora abre em
                    QUALQUER grupo — é a quarta função da semana que existia sem
                    ter caminho. */}
                {onAvisos && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onAvisos(g.grupoId)}
                    data-testid={`wa-avisos-${g.grupoId}`}
                    title="O que este grupo recebe, aviso por aviso — e o que está impedindo."
                  >
                    O que este grupo recebe
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void chamar(url(g, '/interno'), { interno: !g.interno })}
                  data-testid={`wa-interno-${g.grupoId}`}
                  title="Interno = grupo da IGM que NÃO recebe relatório de cliente nenhum. Só internos podem ser o grupo de teste — e marcar desfaz os vínculos com cliente. Um grupo de contratante (que deve receber os relatórios dos clientes dele) NÃO é interno: acrescente os clientes dele com Acompanhar cliente."
                >
                  {g.interno ? 'Não é interno' : 'É da IGM'}
                </Button>
              </div>
            </div>

            {/* O PAPEL do grupo interno — dois, desde 13/09 (a sobra saiu).
                ⚠️ O botão do opt-out NÃO mora mais aqui: ele subiu para a linha
                do grupo e abre para todos, com ou sem papel. */}
            {g.interno && (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3 dark:border-line-dark">
                <label
                  className="text-xs text-ink-3 dark:text-ink-dark-3"
                  htmlFor={`wa-papel-${g.grupoId}`}
                >
                  Papel deste grupo interno
                </label>
                <HelpTip texto="O papel decide antes dos interruptores. Supervisão faz o grupo entrar nos avisos de todos os clientes, com o nome do cliente na frente — e 'O que este grupo recebe' escolhe quais tipos chegam. Silencioso não recebe nada, mesmo com avisos ligados lá. E quando um cliente não tem grupo mapeado, o aviso dele não vai para lugar nenhum: fica registrado na Trilha, com o motivo." />
                <Select
                  id={`wa-papel-${g.grupoId}`}
                  value={g.papel ?? 'silencioso'}
                  onChange={(e) => void chamar(url(g, '/papel'), { papel: e.target.value })}
                  data-testid={`wa-papel-${g.grupoId}`}
                  className="max-w-xs"
                >
                  <option value="supervisao">Supervisão — acompanha todos os clientes</option>
                  <option value="silencioso">Silencioso — não recebe nada, mesmo com avisos ligados</option>
                </Select>
              </div>
            )}

            {/* Escolher/acrescentar cliente — do v2 OU do Auvo. */}
            {trocando === g.grupoId && !g.interno && (
              <div className="mt-3 space-y-2 border-t border-line pt-3 dark:border-line-dark">
                {(g.sugestaoClienteId || g.sugestaoAuvoCodigo) && g.destinatarios.length === 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-ink-2 dark:text-ink-dark-2">
                      Sugestão: {g.sugestaoClienteNome ?? g.sugestaoAuvoNome}
                      {g.sugestaoAuvoCodigo ? ' (do Auvo)' : ''}
                    </span>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setTrocando(null);
                        void chamar(
                          url(g, '/vincular'),
                          g.sugestaoClienteId
                            ? { clienteId: g.sugestaoClienteId }
                            : { sistema: 'auvo', codigo: g.sugestaoAuvoCodigo },
                        );
                      }}
                      data-testid={`wa-aceitar-sugestao-${g.grupoId}`}
                    >
                      Vincular ao sugerido
                    </Button>
                  </div>
                )}

                {/* 🔴 08/10/2026 — DOIS CAMINHOS, cada um da fonte do seu fato (régua do Matheus: "a mensagem toda
                    sai da fonte do fato"). O cadastro do AUVO é o caminho da OS — a tarefa nasce lá. O cliente do
                    SISTEMA é o caminho do monitoramento — o equipamento é dele — e das escolhas da ficha do cliente.
                    O incidente não foi existirem dois campos: foi a OS ser roteada pelo cliente do sistema. */}
                <div>
                  <label
                    htmlFor={`wa-auvo-${g.grupoId}`}
                    className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3"
                  >
                    Cadastro do cliente no Auvo — é por aqui que a OS finalizada e o técnico a caminho
                    encontram este grupo. Vale para qualquer cliente: condomínio, empresa ou contratante.
                  </label>
                  {clientesAuvo.length > 0 ? (
                    <Select
                      id={`wa-auvo-${g.grupoId}`}
                      value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        setTrocando(null);
                        void chamar(url(g, '/vincular'), { sistema: 'auvo', codigo: e.target.value });
                      }}
                      data-testid={`wa-auvo-${g.grupoId}`}
                    >
                      <option value="">Escolha o cadastro do Auvo…</option>
                      {clientesAuvo.map((c) => (
                        <option key={c.codigo} value={c.codigo}>
                          {c.nome} (código {c.codigo})
                        </option>
                      ))}
                    </Select>
                  ) : (
                    // ⚠️ Antes, lista que falhava ao carregar fazia o campo SUMIR sem aviso — e ele é o caminho da OS.
                    <p className="text-xs text-warning dark:text-[#D9AE6B]" data-testid={`wa-auvo-indisponivel-${g.grupoId}`}>
                      A lista de clientes do Auvo não carregou — recarregue a página. Sem ela não dá para ligar o
                      cadastro do Auvo, e é por ele que a OS chega a este grupo.
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`wa-cliente-${g.grupoId}`}
                    className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3"
                  >
                    Cliente no sistema — por aqui chegam os avisos de monitoramento (o equipamento é do cliente
                    do sistema) e valem as escolhas feitas na ficha do cliente. Não leva a OS: a OS vem pelo Auvo.
                  </label>
                  {/* O seletor canônico do v2, que procura no servidor a cada tecla. */}
                  <ClienteV2Combobox
                    id={`wa-cliente-${g.grupoId}`}
                    value=""
                    placeholder="Procurar cliente do sistema por nome ou CNPJ…"
                    onChange={(clienteId) => {
                      if (!clienteId) return;
                      setTrocando(null);
                      void chamar(url(g, '/vincular'), { clienteId });
                    }}
                  />
                </div>

                {/* 🔴 A TERCEIRA dimensão (mig 576): acompanhar um TIPO DE
                    TAREFA — toda OS cujo tipo contenha o marcador chega aqui,
                    de qualquer cliente, sem lista para manter. */}
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-48 flex-1">
                    <label
                      htmlFor={`wa-tipo-${g.grupoId}`}
                      className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3"
                    >
                      …ou um tipo de tarefa (o pedaço que o nome do tipo contém)
                    </label>
                    <Input
                      id={`wa-tipo-${g.grupoId}`}
                      placeholder={'Ex.: - Route'}
                      value={marcadorTipo[g.grupoId] ?? ''}
                      onChange={(e) =>
                        setMarcadorTipo((m) => ({ ...m, [g.grupoId]: e.target.value }))
                      }
                      data-testid={`wa-tipo-${g.grupoId}`}
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={(marcadorTipo[g.grupoId] ?? '').trim().length < 3}
                    onClick={() => {
                      const codigo = (marcadorTipo[g.grupoId] ?? '').trim();
                      if (codigo.length < 3) return;
                      setTrocando(null);
                      setMarcadorTipo((m) => ({ ...m, [g.grupoId]: '' }));
                      void chamar(url(g, '/vincular'), { sistema: 'tipo-tarefa', codigo });
                    }}
                    data-testid={`wa-tipo-acompanhar-${g.grupoId}`}
                  >
                    Acompanhar o tipo
                  </Button>
                </div>

                <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                  Cada aviso encontra o grupo pela fonte de onde nasce: a OS e o técnico a caminho pelo
                  cadastro do Auvo; o monitoramento pelo cliente do sistema. Um grupo de condomínio
                  normalmente tem os dois. Todo vínculo novo entra como proposta e só passa a valer depois
                  de você marcar Conferido, olhando o nome real do grupo. Um grupo pode acompanhar mais de
                  um cliente, e também tipos de tarefa (o monitoramento não tem tipo e nunca chega por aí).
                </p>
              </div>
            )}
            </div>
            )}
          </div>
        ))}

        {filtrados.length === 0 && (
          <p className="text-sm text-ink-3 dark:text-ink-dark-3">
            Nenhum grupo com esse nome.
          </p>
        )}
      </div>
    </div>
  );
}
